// ============================================================================
// Receipt Creation — production React replica of the Evalueserve Figma file.
// Single-file app. Exact palette / type / measurements from the .fig source.
// AI receipt flow (4-step tracker) vs. manual Ariba process (8-step walk).
// ============================================================================
import React, { useState, useCallback } from "react";

const TRACKER = ["1  Agent trigger detected", "2  Teams notification to Agent", "3  AI receipt assistant", "4  Receipt pushed to Ariba"];

const TRIGGERS = [
  { key: "proactive", icon: "bell", title: "Proactive reminder", subtitle: "Before any invoice arrives",
    details: ["Delivery confirmed on PO-100265 (ASN posted 3 days ago)", "No goods/service receipt in Ariba against the PO line", "Agent nudges the Agent to post the receipt so payment is never held"],
    summaryTrigger: "Proactive receipt reminder",
    explanation: "AI matched the delivery confirmation to PO-100265 and identified Priya Anand (Agent) as the PR raiser." },
  { key: "invoice", icon: "mail", title: "Invoice received, receipt missing", subtitle: "After the invoice lands in Ariba",
    details: ["Invoice INV-887750 received in the Ariba inbox", "Matched to PO-100265 — no receipt on file, invoice blocked", "Agent escalates to the Agent to clear the block before due date"],
    summaryTrigger: "Invoice received without receipt",
    explanation: "AI matched the invoice to PO-100265 and identified Priya Anand (Agent) as the PR raiser." },
];
const TRIGGER_FACTS = { po: "PO-100265", supplier: "Nexlink Networks", value: "$45,000", status: "No goods receipt" };

const TEAMS_CHATS = [
  { name: "Procur.AI Agent", preview: "Action needed: receipt for ...", time: "now", avatar: "agent" },
  { name: "Mohan Krishnan", preview: "Can you approve the PR to...", time: "10:12", avatar: "person" },
  { name: "Sourcing Ops", preview: "Weekly supplier sync notes", time: "9:04", avatar: "person" },
  { name: "Finance Team", preview: "Month-end close checklist", time: "Yest", avatar: "person" },
];
const TEAMS_RAIL = ["Activity", "Chat", "Teams", "Calendar", "Files"];
const RECEIPT_CARD = {
  title: "Action needed — goods/service receipt",
  subtitle: "Receipt due for PO-100265 — confirm what was received before the invoice arrives",
  rows: [
    ["Invoice", "INV-887750 · Jul 22, 2026"], ["Purchase Order", "PO-100265 (PR-4902)"],
    ["Supplier", "Nexlink Networks · SUP-4550"], ["Line item", "Network Switch Unit (48-port, managed)"],
    ["Invoiced qty", "300 units @ $150"], ["Net / Tax / Freight", "$45,000 · $2,250 · $900"],
    ["Payable", "$48,150", true], ["Terms / Due", "Net 45 · Sep 5, 2026"],
  ],
};

const CHAT_PROMPT = "Hi Priya Anand - I can create the receipt for you. Did you receive the goods or a service? If goods, just tell me the quantity you actually received.";
const RECEIPT_OPTIONS = ["I received the service in full", "I received 280 units", "I received all 300 units"];

// Ariba auto-filled form (19 of 22 fields from one sentence)
const ARIBA_FIELDS = [
  ["Receipt No.", "GR-2026-004417"], ["Receipt Type", "Standard (service entry)"], ["Purchase Order", "PO-100265"],
  ["PO Line", "10 — Network Switch Unit (48-port, managed)"], ["Supplier", "Nexlink Networks"], ["Supplier Code", "SUP-4550"],
  ["Invoice Reference", "INV-887750"], ["Contract", "C-91002"], ["Ordered Qty", "300"],
  ["Received Qty", "300"], ["Open Qty", "0"], ["Unit Price", "$150"],
  ["Receipt Value", "$45,000"], ["Currency", "USD", true], ["Delivery Date", "Jul 24, 2026"],
  ["Ship-to Plant", "Plant 02 — Coimbatore"], ["Cost Center", "CC-4410 — Network Infrastructure"], ["GL Account", "6120 — IT Equipment"],
  ["Inspection Status", "Accepted", true], ["Requester", "Priya Anand"], ["Approver", "S. Ramesh — Plant Controller", true],
];
const ARIBA_COMMENT = "Full receipt confirmed by Priya Anand in Teams.";

// Manual Ariba walk — 8 steps with metric progression
const MANUAL_STEPS = ["1. PO Lookup", "2. PO Details", "3. Delivery / GRN Log", "4. Invoice", "5. Determine Type", "6. Create Receipt", "7. Log Note", "8. Submitted"];
const MANUAL_PANEL_TITLE = ["Ariba — PO Lookup", "Ariba — PO Details", "Ariba — Delivery / GRN Log", "Ariba — Invoice", "Ariba — Determine Type", "Ariba — Create Receipt", "Ariba — Log Note", "Ariba — Submitted"];
const MANUAL_METRICS = [
  { t: "0:00", s: "0", l: "0" }, { t: "0:42", s: "2", l: "1" }, { t: "1:02", s: "3", l: "2" },
  { t: "1:33", s: "4", l: "3" }, { t: "1:47", s: "5", l: "3" }, { t: "2:12", s: "6", l: "3" },
  { t: "3:57", s: "14", l: "3" }, { t: "3:57", s: "15", l: "3" },
];
const MANUAL_DESC = [
  "Search for the purchase order to begin the receipt creation process.",
  "Read the order details and note them — you'll re-key these later.",
  "Cross-reference what has actually been received so far — you'll need to compare this against the PO yourself.",
  "Check the invoiced quantity/amount against what was received — again, no calculation is done for you.",
  "Based on what you've seen across the last three screens, determine the receipt type yourself — Standard, Partial, or Negative.",
  "Key in every field by hand. Use the earlier screens to re-verify anything you're unsure of.",
  "There's no automatic audit trail — log what you just did somewhere so there's a record.",
  "",
];
const PO_DETAILS = { cols: ["PO NUMBER", "SUPPLIER", "ITEM", "ORDERED QTY", "ORDERED AMOUNT"], row: ["PO-100234", "SUP-4521", "Precision Bearing Assembly", "500", "$25,000"] };
const GRN_LOG = { cols: ["DATE", "QTY RECEIVED", "NOTE"], row: ["Jul 22, 2024", "500", "Full shipment received at Dock 3."] };
const INVOICE_TBL = { cols: ["INVOICE NUMBER", "INVOICED QTY", "INVOICED AMOUNT", "DATE"], row: ["INV-88701", "600", "$25,000", "Jul 21, 2026"] };
const CREATE_FIELDS = [
  ["PO Number", "PO-100234", false], ["Quantity", "e.g. 500", true], ["Receipt Type", "Select...", true],
  ["Item", "Precision Bearing Assembly", false], ["Amount", "e.g. 25000", true], ["Receipt Date", "e.g. Jul 24, 2026", true],
];

const TOAST = { title: "Receipt created", sub: "Receipt GR-2026-004417 has been created and posted to Ariba." };

// ============================== ICONS =======================================
const svg = (p) => ({ viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg", ...p });
const Ic = {
  search: (p) => (<svg {...svg(p)}><circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/><path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  bell: (p) => (<svg {...svg(p)}><path d="M6 8a6 6 0 1 1 12 0c0 7 3 8 3 8H3s3-1 3-8" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/><path d="M10 21a2 2 0 0 0 4 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>),
  mail: (p) => (<svg {...svg(p)}><rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.7"/><path d="m4 7 8 6 8-6" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/><path d="m14 14 3 3 5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  sparkles: (p) => (<svg {...svg(p)}><path d="M12 3l1.8 4.9L18.7 9.7 13.8 11.5 12 16.4 10.2 11.5 5.3 9.7 10.2 7.9 12 3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M19 14l.7 1.9 1.9.7-1.9.7L19 19.2 18.3 17.3 16.4 16.6l1.9-.7L19 14Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>),
  grid: (p) => (<svg {...svg(p)}><rect x="4" y="4" width="7" height="7" rx="1.4" stroke="currentColor" strokeWidth="2"/><rect x="13" y="4" width="7" height="7" rx="1.4" stroke="currentColor" strokeWidth="2"/><rect x="4" y="13" width="7" height="7" rx="1.4" stroke="currentColor" strokeWidth="2"/><rect x="13" y="13" width="7" height="7" rx="1.4" stroke="currentColor" strokeWidth="2"/></svg>),
  chevron: (p) => (<svg {...svg(p)}><path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  arrowRight: (p) => (<svg {...svg(p)}><path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  check: (p) => (<svg {...svg(p)}><path d="m5 12 4.5 4.5L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  x: (p) => (<svg {...svg(p)}><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  pie: (p) => (<svg {...svg(p)}><path d="M12 3a9 9 0 1 0 9 9h-9V3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M14 3.5A9 9 0 0 1 20.5 10H14V3.5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>),
  folder: (p) => (<svg {...svg(p)}><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>),
  trending: (p) => (<svg {...svg(p)}><path d="M3 17l6-6 4 4 8-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M16 7h5v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  globe: (p) => (<svg {...svg(p)}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/><path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" stroke="currentColor" strokeWidth="1.6"/></svg>),
  cloud: (p) => (<svg {...svg(p)}><path d="M7 15a4 4 0 0 1 .5-8 5 5 0 0 1 9.5 1.5A3.5 3.5 0 0 1 17 15H7Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="m12 13-2 4h3l-2 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  cpu: (p) => (<svg {...svg(p)}><rect x="7" y="7" width="10" height="10" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M10 2v3M14 2v3M10 19v3M14 19v3M2 10h3M2 14h3M19 10h3M19 14h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  briefcase: (p) => (<svg {...svg(p)}><rect x="3" y="7" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/></svg>),
  shield: (p) => (<svg {...svg(p)}><path d="M12 3l7 3v5c0 5-3.5 8-7 10-3.5-2-7-5-7-10V6l7-3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M12 8v4M12 15.5v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  file: (p) => (<svg {...svg(p)}><path d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M13 3v5h5M8 13h8M8 17h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  plus: (p) => (<svg {...svg(p)}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/><path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  circleCheck: (p) => (<svg {...svg(p)}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/><path d="m8.5 12 2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  alert: (p) => (<svg {...svg(p)}><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/><path d="M12 7.5v5M12 16v.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  receipt: (p) => (<svg {...svg(p)}><path d="M6 3h12v18l-2-1.3-2 1.3-2-1.3-2 1.3-2-1.3L6 21V3Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M9 8h6M9 12h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>),
  scale: (p) => (<svg {...svg(p)}><path d="M12 4v16M6 20h12M4 8l4-2 4 2M20 8l-4-2-4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 8l-2 5a3 3 0 0 0 6 0L6 8M20 8l-2 5a3 3 0 0 0 6 0l-2-5" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>),
  card: (p) => (<svg {...svg(p)}><rect x="2.5" y="5" width="19" height="14" rx="2.5" stroke="currentColor" strokeWidth="2"/><path d="M2.5 9.5h19" stroke="currentColor" strokeWidth="2"/></svg>),
  logout: (p) => (<svg {...svg(p)}><path d="M9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><path d="M15 8l4 4-4 4M9 12h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>),
};
// ============================== TOP BAR (header block + tracker) ============
function TopBar({ activeStep, mode, onToggleMode, onReset }) {
  return (
    <div className="rc-topbar">
      <div className="rc-topbar-heading-row">
        <div className="rc-topbar-titles">
          <span className="rc-eyebrow">Invoice &amp; PayMents</span>
          <h1 className="rc-page-title">Receipt Creation</h1>
        </div>
        <div className="rc-topbar-actions">
          {mode === "ai" ? (
            <button onClick={onToggleMode} className="rc-button rc-button-secondary rc-button-mode-ai">
              <Ic.grid width={11} height={11} />Manual Process
            </button>
          ) : (
            <button onClick={onToggleMode} className="rc-button rc-button-gradient rc-button-mode-manual">
              <Ic.sparkles width={14} height={14} />Back to AI view
            </button>
          )}
          <button onClick={onReset} className="rc-button rc-button-secondary rc-button-reset">Reset</button>
        </div>
      </div>
      <div className="rc-tracker">
        {TRACKER.map((label, i) => {
          const on = i + 1 === activeStep;
          return (
            <React.Fragment key={i}>
              <span className={`rc-tracker-step${on ? " is-active" : ""}`}>{label}</span>
              {i < TRACKER.length - 1 && <span className="rc-tracker-separator">›</span>}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ============================== STEP 1: TRIGGER =============================
function TriggerStep({ selected, onSelect, onSend }) {
  return (
    <div className="rc-trigger-panel">
      <div className="rc-trigger-intro">
        <div className="rc-trigger-heading">
          <span className="rc-trigger-heading-text">How the agent starts the conversation</span>
          <span className="rc-monitored-badge">
            <Ic.sparkles width={9} height={9} />AGENT MONITORED
          </span>
        </div>
        <span className="rc-trigger-help">The agent watches open POs and the invoice inbox continuously and notifies the Agent in two cases.</span>
      </div>
      <div className="rc-trigger-options">
        {TRIGGERS.map((t) => {
          const on = selected === t.key;
          const I = t.icon === "bell" ? Ic.bell : Ic.mail;
          return (
            <button key={t.key} className="rc-trigger-card" onClick={() => onSelect(t.key)} aria-pressed={on}>
              <div className="rc-trigger-card-heading">
                <div className="rc-trigger-icon"><I width={14} height={14} className="rc-trigger-icon-glyph" /></div>
                <div className="rc-trigger-card-titles">
                  <span className="rc-trigger-card-title">{t.title}</span>
                  <span className="rc-trigger-card-subtitle">{t.subtitle}</span>
                </div>
              </div>
              <div className="rc-trigger-details">
                {t.details.map((d, i) => (
                  <div key={i} className="rc-trigger-detail"><span className="rc-trigger-detail-dot">·</span><span className="rc-trigger-detail-text">{d}</span></div>
                ))}
              </div>
              <span className={`rc-trigger-status${on ? " is-selected" : ""}`}>{on ? "Trigger selected" : "Use this trigger"}</span>
            </button>
          );
        })}
      </div>
      {selected && (() => {
        const t = TRIGGERS.find((x) => x.key === selected);
        const facts = [["TRIGGER", t.summaryTrigger], ["PO", TRIGGER_FACTS.po], ["SUPPLIER", TRIGGER_FACTS.supplier], ["VALUE", TRIGGER_FACTS.value], ["STATUS", TRIGGER_FACTS.status, true]];
        return (
          <div className="rc-trigger-summary rc-fade">
            <div className="rc-trigger-summary-accent" />
            <div className="rc-trigger-facts">
              {facts.map(([l, v, pink], i) => (
                <div key={i} className="rc-trigger-fact">
                  <span className="rc-trigger-fact-label">{l}</span>
                  <span className={`rc-trigger-fact-value${pink ? " is-alert" : ""}`}>{v}</span>
                </div>
              ))}
            </div>
            <span className="rc-trigger-explanation">{t.explanation}</span>
            <button onClick={onSend} className="rc-button rc-button-gradient rc-button-send">
              <Ic.sparkles width={12} height={12} />Send notification to the Agent who raised the PR
            </button>
          </div>
        );
      })()}
    </div>
  );
}

// ============================== TEAMS MOCK ==================================
function TeamsMock({ children, defaultSelectedChat = null }) {
  const [selectedChat, setSelectedChat] = useState<string | null>(defaultSelectedChat);

  return (
    <div className="rc-teams-window">
      <div className="rc-teams-titlebar">
        <span className="rc-teams-title">Microsoft Teams — Priya Anand's workspace</span>
      </div>
      <div className="rc-teams-layout">
        <div className="rc-teams-rail">
          {TEAMS_RAIL.map((r, i) => {
            const on = r === "Chat";
            return (
              <div key={i} className={`rc-teams-rail-item${on ? " is-active" : ""}`}>
                <div className="rc-teams-rail-icon" />
                <span className="rc-teams-rail-label">{r}</span>
              </div>
            );
          })}
        </div>
        <div className="rc-teams-chat-list">
          <div className="rc-teams-chat-list-title"><span>Chat</span></div>
          {TEAMS_CHATS.map((c) => {
            const isSelected = selectedChat === c.name;
            return (
            <button key={c.name} type="button" onClick={() => setSelectedChat(c.name)} aria-pressed={isSelected} className="rc-teams-chat-item">
              <div className={`rc-teams-avatar is-${c.avatar}`} />
              <div className="rc-teams-chat-copy">
                <span className="rc-teams-chat-name">{c.name}</span>
                <span className="rc-teams-chat-preview">{c.preview}</span>
              </div>
              <span className="rc-teams-chat-time">{c.time}</span>
            </button>
          ); })}
        </div>
        <div className="rc-teams-conversation">
          {selectedChat ? (
            <>
              <div className="rc-teams-conversation-header">
                <div className={`rc-teams-avatar is-${TEAMS_CHATS.find((chat) => chat.name === selectedChat)?.avatar || "agent"}`} />
                <div className="rc-teams-conversation-copy">
                  <span className="rc-teams-chat-name">{selectedChat}</span>
                  <span className="rc-teams-chat-subtitle">{selectedChat === "Procur.AI Agent" ? "Bot · Procurement automation" : "Chat"}</span>
                </div>
              </div>
              <div className="rc-teams-messages">
                {selectedChat === "Procur.AI Agent" ? children : <span className="rc-teams-empty-message">No new activity in this chat.</span>}
              </div>
            </>
          ) : (
            <div className="rc-teams-empty-state">Select a chat to view its conversation.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---- Step 2: notification card in the Teams conversation ----
function NotificationCard({ onAssistant }) {
  return (
    <div>
      <div className="rc-notification-author">
        <div className="rc-teams-avatar is-agent" />
        <span>Procur.AI Agent just now</span>
      </div>
      <div className="rc-notification-card">
        <div className="rc-notification-heading">
          <span className="rc-notification-title">{RECEIPT_CARD.title}</span>
          <span className="rc-notification-subtitle">{RECEIPT_CARD.subtitle}</span>
        </div>
        <div className="rc-notification-body">
          {RECEIPT_CARD.rows.map(([l, v, bold], i) => (
            <div key={i} className="rc-notification-row">
              <span className="rc-notification-label">{l}</span>
              <span className={`rc-notification-value${bold ? " is-bold" : ""}`}>{v}</span>
            </div>
          ))}
          <div className="rc-notification-actions">
            <button className="rc-button rc-button-teams-secondary">Create receipt in Ariba</button>
            <button onClick={onAssistant} className="rc-button rc-button-teams-primary">
              <Ic.sparkles width={11} height={11} />AI Assistant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Step 3: AI Receipt Assistant chat ----
function AssistantChat({ onAutofill, formShown }) {
  const [selectedResponse, setSelectedResponse] = useState<string | null>(null);
  const assistantReply = selectedResponse === "I received the service in full"
    ? "Understood - service confirmed as delivered in full. I'll raise a service entry receipt for the full invoiced value $45,000 against PO-100265. No quantity discrepancy to reconcile."
    : selectedResponse === "I received 280 units"
      ? "Understood - 280 of 300 units were received. I'll prepare a partial receipt for 280 units and leave the remaining 20 units open."
      : "Understood - all 300 units were received. I'll prepare a receipt for the full quantity against PO-100265.";

  return (
    <div className="rc-assistant-chat">
      <div className="rc-assistant-title-row">
        <span className="rc-assistant-title">AI Receipt Assistant</span>
        <span className="rc-assistant-caption">in-chat</span>
      </div>
      <div className="rc-assistant-bubble">
        <span className="rc-assistant-message">{CHAT_PROMPT}</span>
        {!selectedResponse && (
          <div className="rc-assistant-options">
            {RECEIPT_OPTIONS.map((option) => (
              <button key={option} type="button" onClick={() => setSelectedResponse(option)}
                className="rc-assistant-option">
                {option}
              </button>
            ))}
          </div>
        )}
      </div>
      {selectedResponse && (
        <>
          <div className="rc-user-bubble">
            <span>{selectedResponse}</span>
          </div>
          <div className="rc-assistant-bubble rc-assistant-reply">
            <span className="rc-assistant-message">{assistantReply}</span>
            <button onClick={onAutofill} disabled={formShown} className={`rc-button rc-button-gradient rc-button-autofill${formShown ? " is-disabled" : ""}`}>
              <Ic.sparkles width={12} height={12} />Auto-fill the Ariba receipt form
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ============================== ARIBA AUTO-FILL FORM =======================
function AribaForm({ onPush, onSeeManual }) {
  return (
    <div className="rc-ariba-form rc-fade">
      <div className="rc-ariba-heading-row">
        <span className="rc-ariba-title">Ariba “Create Receipt” form — normally 22 manual fields</span>
        <span className="rc-ariba-badge">AI FILLED 19 OF 22 FIELDS FROM ONE SENTENCE</span>
      </div>
      <span className="rc-ariba-breadcrumb">SAP Ariba Buying &nbsp;›&nbsp; Receipts &nbsp;›&nbsp; Create Receipt</span>
      <div className="rc-ariba-grid">
        {ARIBA_FIELDS.map(([label, value, grey], i) => (
          <div key={i} className="rc-ariba-field">
            <span className={`rc-ariba-label${grey ? " is-muted" : ""}`}>{label}</span>
            <div className="rc-ariba-value-box">
              <span className="rc-ariba-value">{value}</span>
            </div>
          </div>
        ))}
        <div className="rc-ariba-field rc-ariba-comments">
          <span className="rc-ariba-label">Comments</span>
          <div className="rc-ariba-value-box">
            <span className="rc-ariba-value">{ARIBA_COMMENT}</span>
          </div>
        </div>
      </div>
      <div className="rc-ariba-actions">
        <button onClick={onPush} className="rc-button rc-button-gradient rc-button-push">
          <Ic.sparkles width={16} height={16} />Push receipt to Ariba
        </button>
        <button onClick={onSeeManual} className="rc-link-button">See how this is keyed manually</button>
      </div>
    </div>
  );
}

// ============================== STEP 4: CONFIRMATION =======================
function Confirmation() {
  return (
    <div className="rc-confirmation rc-fade">
      <div className="rc-confirmation-card">
        <div className="rc-confirmation-icon"><Ic.check width={14} height={14} className="rc-confirmation-check" /></div>
        <span className="rc-confirmation-title">{TOAST.title}</span>
        <span className="rc-confirmation-copy">{TOAST.sub}</span>
      </div>
    </div>
  );
}

// ============================== MANUAL FLOW ================================
function Metric({ label, value }) {
  return (
    <div className="rc-metric">
      <span className="rc-metric-label">{label}</span>
      <span className="rc-metric-value">{value}</span>
    </div>
  );
}

function ManualTable({ tbl }) {
  return (
    <div className="rc-table-scroll rc-scroll">
      <table className="rc-manual-table">
        <thead><tr>{tbl.cols.map((c, i) => (<th key={i} className="rc-manual-table-heading">{c}</th>))}</tr></thead>
        <tbody><tr>{tbl.row.map((v, i) => (<td key={i} className={`rc-manual-table-cell${i === 0 ? " is-emphasized" : ""}`}>{v}</td>))}</tr></tbody>
      </table>
    </div>
  );
}

function NextBtn({ label, onClick }) {
  return <button onClick={onClick} className="rc-button rc-button-gradient rc-button-next">{label}</button>;
}

function ManualPanelBody({ step, onAdvance, onReset }) {
  const desc = MANUAL_DESC[step];
  const descEl = desc ? <span className="rc-manual-description">{desc}</span> : null;
  const inputBox = (txt, muted) => (<div className="rc-manual-input"><span className={muted ? "is-placeholder" : ""}>{txt}</span><Ic.chevron width={16} height={16} className="rc-muted-icon" /></div>);

  let content;
  if (step === 0) content = (<><div className="rc-manual-search-row"><div className="rc-manual-search-input">{inputBox("Select a PO number...", true)}</div><button onClick={onAdvance} className="rc-button rc-button-gradient rc-button-submit">Search</button></div></>);
  else if (step === 1) content = (<><ManualTable tbl={PO_DETAILS} /><NextBtn label="Next: Check Delivery / Receipt Log →" onClick={onAdvance} /></>);
  else if (step === 2) content = (<><ManualTable tbl={GRN_LOG} /><NextBtn label="Next: Check Invoice →" onClick={onAdvance} /></>);
  else if (step === 3) content = (<><ManualTable tbl={INVOICE_TBL} /><NextBtn label="Next: Determine Receipt Type →" onClick={onAdvance} /></>);
  else if (step === 4) content = (<><div className="rc-manual-type-options">{["Standard", "Partial", "Negative"].map((o, i) => (<button key={i} className="rc-button rc-button-ghost rc-button-type">{o}</button>))}</div><NextBtn label="Continue to Create Receipt →" onClick={onAdvance} /></>);
  else if (step === 5) content = (
    <>
      <div className="rc-manual-fields">
        {CREATE_FIELDS.map(([l, v, ph], i) => (
          <div key={i} className="rc-manual-field">
            <span className="rc-manual-field-label">{l}</span>
            <div className="rc-manual-field-value"><span className={ph ? "is-placeholder" : ""}>{v}</span></div>
          </div>
        ))}
      </div>
      <div className="rc-manual-form-actions">
        <button className="rc-button rc-button-ghost">Re-verify PO Details</button><button className="rc-button rc-button-ghost">Re-verify Delivery Log</button>
        <button onClick={onAdvance} className="rc-button rc-button-gradient rc-button-submit">Submit</button>
      </div>
    </>
  );
  else if (step === 6) content = (
    <>
      <div className="rc-manual-note"><span>e.g., Created Standard receipt for PO-100234, 499 units.</span></div>
      <NextBtn label="Save Note & Finish" onClick={onAdvance} />
    </>
  );
  else content = (
    <div className="rc-manual-complete">
      <span className="rc-manual-complete-title">Receipt logged manually</span>
      <span className="rc-manual-complete-copy">Total time: 3:57 · Screens visited: 14 · Manual lookups: 3</span>
      <button onClick={onReset} className="rc-button rc-button-gradient rc-button-run-again">Run Manual Process Again</button>
    </div>
  );
  return <div className="rc-manual-panel-body">{descEl}{content}</div>;
}

function ManualFlow({ step, setStep, onReset }) {
  const m = MANUAL_METRICS[step];
  return (
    <div className="rc-manual-flow">
      <div className="rc-manual-scenario">
        <div className="rc-manual-scenario-selector">
          <div className="rc-manual-scenario-label">Scenario</div>
          <div className="rc-manual-scenario-select">
            <span>Standard — PO-100234 (fully received)</span><Ic.chevron width={16} height={16} className="rc-muted-icon" />
          </div>
          </div>
        <span className="rc-manual-scenario-copy">The Ariba path an Agent walks today, screen by screen.</span>
      </div>
      <div className="rc-manual-metrics">
        <Metric label="Elapsed Time" value={m.t} /><Metric label="Screens Visited" value={m.s} /><Metric label="Manual Lookups" value={m.l} />
      </div>
      <div className="rc-scroll rc-manual-steps-scroll">
        <div className="rc-manual-steps">
          {MANUAL_STEPS.map((s, i) => {
            const on = i === step;
            return (
              <React.Fragment key={i}>
                <span className={`rc-manual-step${on ? " is-active" : ""}`}>{s}</span>
                  {i < MANUAL_STEPS.length - 1 && <Ic.chevron width={12} height={12} className="rc-manual-step-chevron" />}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div className="rc-manual-window">
        <div className="rc-manual-window-titlebar">
          <div className="rc-window-dots">{[0, 1, 2].map((i) => (<span key={i} />))}</div>
          <span>{MANUAL_PANEL_TITLE[step]}</span>
        </div>
        <div className="rc-manual-window-content">
          <ManualPanelBody step={step} onAdvance={() => setStep((s) => Math.min(s + 1, 7))} onReset={onReset} />
        </div>
      </div>
    </div>
  );
}

// ============================== APP =========================================
export function ReceiptCreation() {
  const [mode, setMode] = useState("ai");                 // ai | manual
  const [aiStage, setAiStage] = useState("trigger");      // trigger | teams | assistant | pushed
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [formShown, setFormShown] = useState(false);
  const [manualStep, setManualStep] = useState(0);

  const activeStep = mode === "manual" ? 1 : { trigger: 1, teams: 2, assistant: 3, pushed: 4 }[aiStage];

  const reset = useCallback(() => {
    setMode("ai"); setAiStage("trigger"); setSelectedTrigger(null); setFormShown(false); setManualStep(0);
  }, []);

  return (
    <div className="receipt-creation">
      <div className="rc-content">
        <TopBar activeStep={activeStep} mode={mode}
          onToggleMode={() => setMode((m) => (m === "ai" ? "manual" : "ai"))} onReset={reset} />

        {mode === "manual" ? (
          <ManualFlow step={manualStep} setStep={setManualStep} onReset={() => setManualStep(0)} />
        ) : aiStage === "trigger" ? (
          <TriggerStep selected={selectedTrigger} onSelect={setSelectedTrigger} onSend={() => setAiStage("teams")} />
        ) : aiStage === "teams" ? (
          <TeamsMock><NotificationCard onAssistant={() => setAiStage("assistant")} /></TeamsMock>
        ) : aiStage === "assistant" ? (
          <>
            <TeamsMock defaultSelectedChat="Procur.AI Agent"><AssistantChat onAutofill={() => setFormShown(true)} formShown={formShown} /></TeamsMock>
            {formShown && <AribaForm onPush={() => setAiStage("pushed")} onSeeManual={() => setMode("manual")} />}
          </>
        ) : (
          <Confirmation />
        )}
      </div>
    </div>
  );
}
